import React, { useState, useEffect } from 'react';
import { BOOTH_RULES_CONFIG } from '../services/boothRulesService';
import BoothPicker from './BoothPicker';
import './RulesAdminPanel.css';

const RulesAdminPanel = ({ currentRules, onRulesUpdate, gridData, onSaveRequest, onCloseRequest, onRemoveGroup }) => {
  const [rules, setRules] = useState(currentRules || BOOTH_RULES_CONFIG);
  const [activeTab, setActiveTab] = useState('existing');
  const [newGroupBooths, setNewGroupBooths] = useState('');
  const [showBoothPicker, setShowBoothPicker] = useState(false);
  const [editingGroup, setEditingGroup] = useState({ ruleId: null, groupIndex: null });

  // Update rules when currentRules prop changes
  useEffect(() => {
    if (currentRules) {
      setRules(currentRules);
    }
  }, [currentRules]);

  // Format a parameter key into human-friendly Title Case with spaces
  const formatLabel = (key) => {
    if (!key || typeof key !== 'string') return '';
    const spaced = key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_\-\s]+/g, ' ')
      .trim()
      .toLowerCase();
    if (!spaced) return key;
    return spaced
      .split(' ')
      .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  };

  const handleRuleToggle = (ruleId) => {
    const updatedRules = {
      ...rules,
      [ruleId]: {
        ...rules[ruleId],
        enabled: !rules[ruleId].enabled,
      },
    };
    setRules(updatedRules);
    onRulesUpdate(updatedRules);
  };

  const handleParameterChange = (ruleId, parameter, value) => {
    const updatedRules = {
      ...rules,
      [ruleId]: {
        ...rules[ruleId],
        [parameter]: value,
      },
    };
    setRules(updatedRules);
    onRulesUpdate(updatedRules);
  };

  const handleGroupChange = (ruleId, groupIndex, field, value) => {
    const updated = { ...rules };
    const groups = Array.isArray(updated[ruleId]?.groups) ? [...updated[ruleId].groups] : [];
    groups[groupIndex] = { ...groups[groupIndex], [field]: value };
    updated[ruleId] = { ...updated[ruleId], groups };
    setRules(updated);
    onRulesUpdate(updated);
  };

  const addNewGroup = (ruleId) => {
    const updated = { ...rules };
    const existing = updated[ruleId]?.groups;
    const groups = Array.isArray(existing) ? [...existing] : [];
    groups.push({
      id: `group-${Date.now()}`,
      name: 'New Group',
      booths: [],
      description: 'New booth group',
    });
    updated[ruleId] = { ...updated[ruleId], groups };
    setRules(updated);
    onRulesUpdate(updated);
  };

  const removeGroup = (ruleId, groupIndex) => {
    const updatedRules = { ...rules };
    const groups = Array.isArray(updatedRules[ruleId]?.groups) ? [...updatedRules[ruleId].groups] : [];
    const removed = groups.splice(groupIndex, 1);
    updatedRules[ruleId] = { ...updatedRules[ruleId], groups };
    setRules(updatedRules);
    onRulesUpdate(updatedRules);
    // Notify parent that a group was removed so it can persist changes immediately
    if (removed && removed.length > 0 && typeof onRemoveGroup === 'function') {
      try { onRemoveGroup(ruleId, removed[0]); } catch (e) { console.warn('onRemoveGroup handler failed', e); }
    }
  };

  const parseBoothIds = (input) => {
    if (!input || typeof input !== 'string') return [];

    const pad2 = (n) => n.toString().padStart(2, '0');
    const toGridId = (row, col) => `${pad2(parseInt(row,10))}${pad2(parseInt(col,10))}`;

    const parsedIds = input
      .split(/[\,\s\n]+/)
      .map(id => id.trim().toUpperCase())
      .filter(id => id.length > 0)
      .map(id => {
        // If it's already a 4-digit grid id like 0101
        if (/^\d{4}$/.test(id)) {
          return id;
        }

        // cell-<row>-<col>
        if (id.startsWith('CELL-')) {
          const match = id.match(/^CELL-(\d+)-(\d+)$/i);
          if (match) {
            const [_, r, c] = match;
            return toGridId(r, c);
          }
        }

        // R<row>C<col>
        if (/^R\d+C\d+$/i.test(id)) {
          const match = id.match(/^R(\d+)C(\d+)$/i);
          if (match) {
            const [_, r, c] = match;
            return toGridId(r, c);
          }
        }

        // Row-Col like 1-2
        if (/^\d+-\d+$/.test(id)) {
          const [r, c] = id.split('-');
          return toGridId(r, c);
        }

        // Alpha-number like A01 or AA01 -> map by booth.number if possible
        if (/^[A-Z]{1,2}\d{2}$/.test(id)) {
          const booth = (gridData || []).find(b => String(b.number).toUpperCase() === id);
          if (booth) return booth.id;
          // Fallback best-effort: derive row from letters (A=1, AA=27, etc.) and col from digits
          const letters = id.replace(/\d+/g, '');
          const digits = id.replace(/\D+/g, '');
          let row = 0;
          for (let i = 0; i < letters.length; i++) {
            row = row * 26 + (letters.charCodeAt(i) - 64);
          }
          const col = parseInt(digits, 10);
          return toGridId(row, col);
        }

        // Legacy numeric like 101 (assume row*100+col); attempt grid match first
        if (/^\d{3}$/.test(id)) {
          const boothById = (gridData || []).find(b => String(b.id) === id);
          if (boothById) return boothById.id;
          // Try find by number if number is also numeric in some layouts
          const boothByNumber = (gridData || []).find(b => String(b.number) === id);
          if (boothByNumber) return boothByNumber.id;
          const n = parseInt(id, 10);
          const row = Math.floor(n / 100);
          const col = n % 100;
          return toGridId(row, col);
        }

        // Unknown: try direct match on number or id
        const boothDirect = (gridData || []).find(b => String(b.id) === id || String(b.number).toUpperCase() === id);
        if (boothDirect) return boothDirect.id;
        return id; // leave as-is
      });

    return parsedIds;
  };

  const openBoothPicker = (ruleId, groupIndex) => {
    setEditingGroup({ ruleId, groupIndex });
    setShowBoothPicker(true);
  };

  const handleBoothPickerClose = () => {
    setShowBoothPicker(false);
    setEditingGroup({ ruleId: null, groupIndex: null });
  };

  const handleBoothPickerApply = (selectedBooths) => {
    if (editingGroup.ruleId && editingGroup.groupIndex !== null) {
      handleGroupChange(editingGroup.ruleId, editingGroup.groupIndex, 'booths', selectedBooths);
    } else {
      // Missing editing group data; ignore apply
    }
    handleBoothPickerClose();
  };

  const exportRules = () => {
    const rulesJson = JSON.stringify(rules, null, 2);
    const blob = new Blob([rulesJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booth-rules-config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importRules = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedRules = JSON.parse(e.target.result);
          setRules(importedRules);
          onRulesUpdate(importedRules);
        } catch (error) {
          alert('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="rules-admin-panel">
      <div className="admin-header">
        <h2>🔧 Booth Rules Administration</h2>
        <div className="admin-actions">
          <button onClick={exportRules} className="export-btn">
            📤 Export Rules
          </button>
          <label className="import-btn">
            📥 Import Rules
            <input type="file" accept=".json" onChange={importRules} hidden />
          </label>
          <button
            onClick={async () => {
              if (typeof onSaveRequest === 'function') {
                await onSaveRequest();
              }
            }}
            className="action-save-btn"
            title="Save rules to layout"
          >
            💾 Save
          </button>
          <button
            onClick={() => {
              if (typeof onCloseRequest === 'function') {
                onCloseRequest();
              }
            }}
            className="action-close-btn"
            title="Close rules admin"
          >
            ✖ Close
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'existing' ? 'active' : ''}`}
          onClick={() => setActiveTab('existing')}
        >
          Existing Rules
        </button>
        <button 
          className={`tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Booth Groups
        </button>
      </div>

      {activeTab === 'existing' && (
        <div className="rules-list">
          {Object.entries(rules)
            .sort(([, a], [, b]) => {
              const ap = typeof a.priority === 'number' ? a.priority : 999;
              const bp = typeof b.priority === 'number' ? b.priority : 999;
              if (ap !== bp) return ap - bp;
              return (a.name || '').localeCompare(b.name || '');
            })
            .map(([ruleId, rule]) => {
            // Determine which top-level keys are editable (to avoid duplicates with nested params)
            const topLevelEditableEntries = Object.entries(rule)
              .filter(([key, value]) =>
                key !== 'parameters' &&
                (typeof value === 'number' || typeof value === 'string') &&
                key !== 'name' && key !== 'description' && key !== 'type' && key !== 'trigger' && key !== 'enabled'
              );
            const topLevelEditableKeys = topLevelEditableEntries.map(([k]) => k);

            return (
            <div key={ruleId} className={`rule-item ${rule.enabled ? 'enabled' : 'disabled'}`}>
              <div className="rule-header">
                <div className="rule-info">
                  <h4>{rule.name}</h4>
                  <p>{rule.description}</p>
                  <div className="rule-meta">
                    <span className="rule-type">{rule.type}</span>
                    <span className="rule-priority">Priority: {rule.priority}</span>
                    <span className="rule-trigger">Trigger: {rule.trigger}</span>
                  </div>
                </div>
                <div className="rule-controls">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => handleRuleToggle(ruleId)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
              {/* Always show rule parameters for editing, including priority and other top-level params */}
              <div className="rule-parameters">
                {/* Top-level parameters (priority, etc.) */}
                {topLevelEditableEntries.map(([key, value]) => (
                    <div key={key} className="parameter-item">
                      <label className="param-label">{formatLabel(key)}:</label>
                      <input
                        type={typeof value === 'number' ? 'number' : 'text'}
                        value={value}
                        onChange={e => handleParameterChange(ruleId, key, typeof value === 'number' ? parseInt(e.target.value) : e.target.value)}
                      />
                    </div>
                  ))}
                {/* Nested parameters object */}
                {rule.parameters && Object.entries(rule.parameters)
                  .filter(([paramKey]) => !topLevelEditableKeys.includes(paramKey))
                  .map(([paramKey, paramValue]) => (
                  <div key={paramKey} className="parameter-item">
                    <label className="param-label">{formatLabel(paramKey)}:</label>
                    <input
                      type={typeof paramValue === 'number' ? 'number' : 'text'}
                      value={paramValue}
                      onChange={e => handleParameterChange(
                        ruleId,
                        `parameters`,
                        { ...rule.parameters, [paramKey]: typeof paramValue === 'number' ? parseInt(e.target.value) : e.target.value }
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {activeTab === 'groups' && (
        <div className="groups-management">
          {rules.GROUP_PURCHASE && (
            <div className="groups-section">
              <div className="section-header">
                <h3>Booth Groups</h3>
                <button onClick={() => addNewGroup('GROUP_PURCHASE')} className="add-group-btn">
                  ➕ Add Group
                </button>
              </div>
              
              {(rules.GROUP_PURCHASE.groups || []).length === 0 && (
                <div className="empty-groups-state">
                  <p>No groups defined for this layout.</p>
                  <p className="hint">Use "Add Group" to create your first booth group.</p>
                </div>
              )}

              {(rules.GROUP_PURCHASE.groups || []).map((group, index) => (
                <div key={group.id} className="group-editor">
                        <div className="group-header">
                          <div className="group-header-field">
                            <label className="field-label">Name</label>
                            <input
                              type="text"
                              value={group.name}
                              onChange={(e) => handleGroupChange('GROUP_PURCHASE', index, 'name', e.target.value)}
                              className="group-name-input compact"
                              placeholder="Group name"
                            />
                          </div>

                          <div className="group-header-field">
                            <label className="field-label">Description</label>
                            <input
                              type="text"
                              value={group.description}
                              onChange={(e) => handleGroupChange('GROUP_PURCHASE', index, 'description', e.target.value)}
                              className="group-desc-input"
                              placeholder="Short description"
                            />
                          </div>

                          <button 
                            onClick={() => removeGroup('GROUP_PURCHASE', index)}
                            className="remove-group-btn"
                            title="Remove group"
                          >
                            🗑️
                          </button>
                        </div>

                    <div className="group-fields">
                      {/* Minimum Required removed: group rule is now all-or-none */}
                      <div className="field-group">
                      <div className="booth-ids-row">
                        <label className="left-label">Booth IDs:</label>
                        <div className="booth-ids-help">
                          <small className="input-help">
                            Enter booth IDs in any format: • Letter-Number: A01, A02, B15 • R1C1: R1C1, R1C2 • Row-Col: 1-1, 1-2 • Legacy cell: cell-1-1 • Legacy numbers: 101, 102
                          </small>
                        </div>
                      </div>
                      <div className="booth-input-container">
                        <textarea
                          value={group.booths ? group.booths.join(', ') : ''}
                          onChange={(e) => {
                            const parsedBooths = parseBoothIds(e.target.value);
                            handleGroupChange('GROUP_PURCHASE', index, 'booths', parsedBooths);
                          }}
                          className="booths-input"
                          placeholder="Examples: A01, A02, A03 OR R1C1, R1C2, R1C3 OR 1-1, 1-2, 1-3"
                          rows="3"
                        />
                        <button
                          type="button"
                          onClick={() => openBoothPicker('GROUP_PURCHASE', index)}
                          className="visual-picker-btn"
                          title="Open visual booth picker"
                        >
                          📍 Visual Picker
                        </button>
                      </div>
                      <div className="booth-count-info">
                        <small>
                          {group.booths ? group.booths.length : 0} booth(s) defined
                          {group.booths && group.booths.length > 0 && (
                            <span className="booth-preview">
                              {' - '}
                              {group.booths.slice(0, 3).join(', ')}
                              {group.booths.length > 3 && ` + ${group.booths.length - 3} more`}
                            </span>
                          )}
                        </small>
                        {group.booths && group.booths.length > 0 && (
                          <div className="booth-validation">
                            {gridData && gridData.length > 0 ? (
                              (() => {
                                const validBooths = group.booths.filter(boothId => 
                                  gridData.some(b => b.id === boothId)
                                );
                                const invalidBooths = group.booths.length - validBooths.length;
                                
                                return (
                                  <small className={`validation-status ${invalidBooths === 0 ? 'valid' : 'invalid'}`}>
                                    {invalidBooths === 0 
                                      ? `✅ All ${validBooths.length} booth(s) are valid`
                                      : `⚠️ ${invalidBooths} invalid booth ID(s) - ${validBooths.length} valid`
                                    }
                                  </small>
                                );
                              })()
                            ) : (
                              <small className="validation-status neutral">
                                📋 Grid data not available for validation
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Booth Picker Modal */}
      <BoothPicker
        gridData={gridData}
        selectedBooths={editingGroup.ruleId && editingGroup.groupIndex !== null 
          ? (rules[editingGroup.ruleId]?.groups?.[editingGroup.groupIndex]?.booths || [])
          : []
        }
        onBoothsChange={handleBoothPickerApply}
        onClose={handleBoothPickerClose}
        show={showBoothPicker}
      />
    </div>
  );
};

export default RulesAdminPanel;